import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import SchoolIcon from '@material-ui/icons/School';
import PeopleIcon from '@material-ui/icons/People';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import EditIcon from '@material-ui/icons/Edit';
import BarChartIcon from '@material-ui/icons/BarChart';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { SOLUTION } from './solutions';
import { FEATURE_KEY, LEARNCREATOR_START_PATH } from '../../utils/constants';
import { isFeatureEnabled } from '../../utils/productAccess';
import { COS_PREVIEW_PATH } from '../../utils/cpci.util';

/**
 * School Network Workspace navigation.
 *
 * Structure and labels come from the PbDQ Navigation Bar sheet, rows 48-100 —
 * the block headed "(2) School Network Worskpace (SNW) (New workspace)".
 *
 * A network is NOT a new access level: it is an ordinary school-type account
 * (level 22) whose organisation carries `is_school_network: true`, which the session
 * exposes as `authUser.is_school_network`. That flag is the only thing that picks this
 * menu over the School one.
 *
 * Row shape matches schoolWorkspace.nav.js; see partnerWorkspace.nav.js for the
 * field reference. `ctx` is the same object PartialMainList already builds.
 *
 * DIFFERENCES FROM THE SCHOOL MENU, all from the sheet:
 *   · My Schools (SNW_MS, row 66) — a network manages the schools inside it.
 *     The School workspace has no such row.
 *   · Insights holds "School Network Dashboard" (row 96) only — a network has
 *     no School Dashboard (7 Oct 2026; the server does not grant it either).
 *     Class Dashboard is NOT here — a network opens a class's dashboard from
 *     My Classes only.
 *   · Everything else — Home, Solutions, Performance Summary, My Classes,
 *     My Programs, LearnCreator, Settings — matches School row for row.
 */

/** Sheet rows that have no destination yet. Recorded rather than invented. */
export const NETWORK_WORKSPACE_DEFERRED = [];

export const NETWORK_WORKSPACE_NAV = [
    {
        code: 'SNW_HO',
        /**
         * Tutorial step-1 anchor. REQUIRED — TutorialManager force-navigates to a
         * step's route until it finds the target, so losing this id traps the user
         * on Home and breaks every other sidebar item. Same note as PW_HO / SW_HO.
         */
        id: 'sidebar-home-link',
        label: 'Home',
        icon: HomeIcon,
        path: '/my_workspace',
        // Rows 48-50 give Home exactly P0, P1 and LC.
        solutions: [SOLUTION.P0, SOLUTION.P1, SOLUTION.LC],
        visibleWhen: () => true,
    },

    {
        code: 'SNW_SO',
        label: 'Solutions',
        icon: CategoryIcon,
        // Parent navigates only when the drawer is collapsed; otherwise it expands.
        path: '/dq_pro/solutions',
        slideCode: 'CS-Solution-Overview',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        /*
         * Common Section (rows 54-59) — the SAME pages every other workspace
         * shows, so these reuse the existing routes rather than adding copies.
         */
        children: [
            {
                code: 'SNW_SO_OV', label: 'Overview', path: '/dq_pro/solutions/overview',
                slideCode: 'CS-Solution-Overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
            },
            {
                code: 'SNW_SO_P1', label: 'COS Readiness Solution', path: '/dq_pro/solutions/readiness',
                slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SNW_SO_P1_OV', label: 'Overview', path: '/dq_pro/solutions/readiness/overview',
                        slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'SNW_SO_P0', label: 'COS Children Assessment', path: '/dq_pro/solutions/children-assessment',
                slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SNW_SO_P0_OV', label: 'Overview', path: '/dq_pro/solutions/children-assessment/overview',
                        slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        code: 'SNW_SO_P0_PV', label: 'Preview', path: '/dq_pro/solutions/children-assessment/preview',
                        // The path above redirects to the COS catalogue preview page.
                        matchPrefixes: [COS_PREVIEW_PATH],
                        slideCode: 'CS-Solution-P0-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'SNW_SO_LC', label: 'LearnCreator', path: '/dq_pro/solutions/learncreator',
                slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SNW_SO_LC_OV', label: 'Overview', path: '/dq_pro/solutions/learncreator/overview',
                        slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        code: 'SNW_SO_LC_PV', label: 'Preview', path: '/dq_pro/solutions/learncreator/preview',
                        slideCode: 'CS-Solution-LC-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
        ],
    },

    {
        code: 'SNW_GO',
        label: 'Performance Summary',
        icon: TrendingUpIcon,
        path: '/dq_pro/performance-summary',
        slideCode: 'SNW-{P0|P1A|P1B|LC}-Performance Summary',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
    },

    {
        code: 'SNW_MS',
        /** Tutorial anchor (My Schools step). */
        id: 'sidebar-my-schools-link',
        label: 'My Schools',
        icon: SchoolIcon,
        path: '/dq_pro/clients/list',
        slideCode: 'SNW-ALL-My Schools-Main',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        note: 'Row 66. The list is scoped server-side to the schools inside THIS network.',
    },

    {
        code: 'SNW_MC',
        /** Tutorial step-4 anchor, and the Home page's FloatingNudge anchor. */
        id: 'sidebar-classes-link',
        label: 'My Classes',
        icon: PeopleIcon,
        path: '/dq_pro/classes',
        slideCode: 'SNW-ALL-My Classes-Main',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
    },

    {
        code: 'SNW_MP',
        label: 'My Programs',
        icon: EmojiObjectsIcon,
        // Parent expands; the path is only used when the drawer is collapsed.
        path: '/dq_pro/learning/program/list',
        slideCode: 'SNW-{P1A|P1B|LC}-MyPrograms',
        // Rows 91-92: P1A, P1B and LC.
        solutions: [SOLUTION.P1A, SOLUTION.P1B, SOLUTION.LC],
        visibleWhen: (ctx) => ctx.hasAnyProgramAccess,
        children: [
            /*             * NO "Create" ROW.            */          
            {
                code: 'SNW_MP_LP',
                /** Tutorial step-2 anchor. */
                id: 'sidebar-products-link',
                label: 'Learning Programs',
                path: '/dq_pro/learning/program/list',
                solutions: [SOLUTION.P1A, SOLUTION.P1B, SOLUTION.LC],
                visibleWhen: (ctx) => ctx.hasLearningPrograms && ctx.canViewLearningPrograms,
            },
        ],
    },

    {
        code: 'SNW_LC',
        label: 'LearnCreator',
        icon: EditIcon,
        // The LearnCreator landing page, which every entry point opens; typing
        // a goal there starts the builder (bug sheet Ian-LC-30Sep-1).
        path: LEARNCREATOR_START_PATH,
        slideCode: 'SNW-P1B, LC-LearnCreator',
        // Rows 94-95: P1B and LC.
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        /*
         * LearnCreator shows when the partner has ASSIGNED it to this workspace —
         * the partner's "Link Products" > Assign LearnCreator flow turns on the
         * org's learning_program `create` permission and the user's
         * create_learning_program capability. That assignment is what the sheet
         * means by P1B (teacher-created programs). Same rule as Home's Step 4
         * and lcActivated in ManageClasses.
         *
         * `enabled` alone is not that signal: createClient seeds every network
         * with learning_program enabled (view/link only) so it can see the
         * programs its partner delivers — reading it put the row on P1A too.
         */
        visibleWhen: (ctx) => ctx.canCreateLearningPrograms,
    },

    {
        code: 'SNW_IN',
        label: 'Insights',
        icon: BarChartIcon,
        path: (ctx) => ctx.insightsPath,
        solutions: [SOLUTION.ALL],
        // A network's PAC feature is its own key, school_network_dashboard —
        // distinct from a single school's school_dashboard.
        visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD),
        /*
         * Row 96 calls this "School Network Dashboard" — its own route,
         * now the real Network Dashboard (built from the "P1 Network
         * Dashboard" deck, in progress section by section) rather than the
         * earlier "Coming soon" placeholder.
         */
        children: [
            {
                code: 'SNW_IN_SD',
                label: 'School Network Dashboard',
                path: '/dq_pro/network-dashboard/overview',
                slideCode: 'SNW-ALL-Insights-School Network Dashboard',
                solutions: [SOLUTION.ALL],
                visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD),
            },
        ],
    },

    /*
     * Support — added by the SNW, SW Parental Consent Flow (5 Oct 2026, slides
     * 5 and 22): the network's Support page carries the Parental Consent card.
     * Teacher still has none.
     *
     * Settings lives under the Profile Icon (rows 97-100), exactly as elsewhere:
     * Account, Security, Advanced Settings, then Sign out.
     */
    {
        code: 'SNW_SP',
        label: 'Support',
        icon: HelpOutlineIcon,
        path: '/support',
        slideCode: 'SNW-Support',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
    },
];
